/**
 * Advisor Selector functionality
 * Handles the advisor selection dropdown in the header
 * Integrated with existing advisor system from jsc-reset-scripts.js
 */

/** Prefixes root-relative paths with the current Shopify locale URL (e.g. /en, /fr). */
function vwLocalizedPath(pathOrUrl) {
  if (pathOrUrl == null || pathOrUrl === '') return pathOrUrl;
  const s = String(pathOrUrl);
  if (s.indexOf('://') !== -1) return s;
  const path = s.startsWith('/') ? s : '/' + s;
  const root = (window.theme && window.theme.urls && window.theme.urls.rootUrl) || '/';
  const prefix = root === '/' ? '' : String(root).replace(/\/$/, '');
  return prefix ? prefix + path : path;
}

function vwAdvisorStr() {
  return (window.theme && window.theme.strings && window.theme.strings.advisor) || {};
}
function vwAdvisorAssets() {
  return (window.theme && window.theme.assetUrls) || {};
}
function vwCustomerSupportUrl() {
  const raw = (window.theme && window.theme.urls && window.theme.urls.customerSupport) || '/pages/contact-us';
  return vwLocalizedPath(raw);
}

function vwFindVorwerkStoreUrl() {
  const raw = (window.theme && window.theme.urls && window.theme.urls.findVorwerkStore) || '/pages/vorwerk-stores';
  return vwLocalizedPath(raw);
}

class AdvisorSelector {
  constructor() {
    this.vorwerkStorePage = vwFindVorwerkStoreUrl();
    this.selector = document.querySelector('[data-advisor-selector]');
    this.trigger = document.querySelector('[data-advisor-trigger]');
    this.dropdown = document.querySelector('[data-advisor-dropdown]');
    this.idInput = document.querySelector('[data-advisor-id-input]');
    this.nameInput = document.querySelector('[data-advisor-name-input]');
    this.profilePictureInput = document.querySelector('[data-advisor-profile-picture-input]');
    this.container = document.querySelector('.advisor-selector__container');

    this.isOpen = false;
    this.triggerClickHandler = null;
    this.documentClickHandler = null;

    this.init();
  }

  init() {
    if (!this.selector) return;

    this.bindEvents();
    this.validateAndUpdateCartAttributes();
  }

  bindEvents() {
    // Remove existing event listeners to avoid duplicates
    if (this.triggerClickHandler) {
      this.trigger?.removeEventListener('click', this.triggerClickHandler);
    }
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
    }

    // Toggle dropdown
    this.triggerClickHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggleDropdown();
    };
    this.trigger?.addEventListener('click', this.triggerClickHandler);

    // Close dropdown when clicking outside
    this.documentClickHandler = (e) => {
      if (!this.selector.contains(e.target)) {
        this.closeDropdown();
      }
    };
    document.addEventListener('click', this.documentClickHandler);
  }

  validateAndUpdateCartAttributes() {
    // Get current values from hidden inputs (which reflect cart attributes)
    const currentCartId = this.idInput?.value || '';
    const currentCartName = this.nameInput?.value || '';
    const currentCartProfile = this.profilePictureInput?.value || '';

    // Get current values from localStorage
    const localStorageId = localStorage.getItem('Advisor ID') || '';
    const localStorageName = localStorage.getItem('Advisor Name') || '';
    const localStorageProfile = localStorage.getItem('Advisor Profile Picture') || '';

    // If cart attributes are empty/null/undefined/"-", use localStorage values
    const effectiveId = (currentCartId && currentCartId !== '-' && currentCartId !== 'null' && currentCartId !== 'undefined') 
      ? currentCartId : localStorageId;
    const effectiveName = (currentCartName && currentCartName !== '-' && currentCartName !== 'null' && currentCartName !== 'undefined') 
      ? currentCartName : localStorageName;
    const effectiveProfile = (currentCartProfile && currentCartProfile !== '-' && currentCartProfile !== 'null' && currentCartProfile !== 'undefined') 
      ? currentCartProfile : localStorageProfile;

    // Check if there's a mismatch between effective values and localStorage
    const needsUpdate =
      effectiveId !== localStorageId ||
      effectiveName !== localStorageName ||
      effectiveProfile !== localStorageProfile;

    if (needsUpdate && effectiveId && effectiveName) {
      // console.log('Cart attributes out of sync, updating...', {
      //   cart: { id: currentCartId, name: currentCartName, profile: currentCartProfile },
      //   effective: { id: effectiveId, name: effectiveName, profile: effectiveProfile },
      //   localStorage: { id: localStorageId, name: localStorageName, profile: localStorageProfile }
      // });

      // Use the centralized updateAdvisorData function if available
      if (typeof updateAdvisorData === 'function') {
        updateAdvisorData(effectiveId, effectiveName, effectiveProfile, false);
      } else {
        localStorage.setItem('Advisor ID', effectiveId);
        localStorage.setItem('Advisor Name', effectiveName);
        localStorage.setItem('Advisor Profile Picture', effectiveProfile);
        var storepointID = window.location.href.split('selected=')[1];
        localStorage.setItem('Advisor Storepoint ID', storepointID);

        // Update cart attributes with effective values
        const attributes = {
          'Advisor ID': effectiveId,
          'Advisor Name': effectiveName
        };

        CartJS.setAttributes(attributes);
      }
    }
    this.updateDisplayFromLocalStorage();
  }

  updateDisplayFromLocalStorage() {
    const advisorId = localStorage.getItem('Advisor ID');
    const advisorName = localStorage.getItem('Advisor Name');
    const advisorProfilePicture = localStorage.getItem('Advisor Profile Picture') || '';

    if (advisorId && advisorName) {
      document.body.classList.add('advisor-is-selected');
      this.updateDisplay(advisorId, advisorName, advisorProfilePicture);
    } else {
      document.body.classList.remove('advisor-is-selected');
    }
  }

  updateDisplay(id, name, profilePicture) {
    // Update hidden inputs
    if (this.idInput) this.idInput.value = id;
    if (this.nameInput) this.nameInput.value = name;
    if (this.profilePictureInput) this.profilePictureInput.value = profilePicture;

    // Update button text
    const currentElement = this.trigger?.querySelector('.advisor-selector__current');
    if (currentElement) {
      let profileImgHtml = '';
      if (profilePicture && profilePicture !== '' && profilePicture !== '-') {
        profileImgHtml = `<div class="advisor-selector__profile-picture"><img src="${profilePicture}" alt="${name}" width="40" height="40" loading="lazy"></div>`;
      }
      else {
        profileImgHtml = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path fill="currentColor" d="M9 14c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0-6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zM9 15c-3.9 0-7 3.1-7 7 1 0 1.9-.6 2.3-1.6.6-2 2.5-3.4 4.7-3.4s4.1 1.4 4.7 3.4c.3 1 1.2 1.6 2.3 1.6 0-3.9-3.1-7-7-7z"/>
              <path fill="currentColor" d="M10 0v4.5c.7.1 1.4.4 2 .8V2h8v5h-2.9l-1.9 1.9c-.8.8-.8 2 0 2.8L17.9 9H22V0H10z"/>
            </svg>`;
      }

      currentElement.innerHTML = `
        ${profileImgHtml}
      `;
    }

    // Update cart display if it exists
    const cartAdvisorName = document.getElementById('jsc-cart-advisor-name');
    if (cartAdvisorName) {
      cartAdvisorName.textContent = name;
    }

    const cartAdvisorNameInput = document.getElementById('jsc-cart-advisor-name-input');
    if (cartAdvisorNameInput) {
      cartAdvisorNameInput.value = name;
    }

    // Ensure we're showing the dropdown state
    this.showDropdownState(profilePicture);
    if (id && id !== '' && id !== '-') {
      this.selector.classList.add('has-advisor-selected');
    } else {
      this.selector.classList.remove('has-advisor-selected');
    }
  }

  showDropdownState(profilePicture) {
    if (!this.container) return;

    // Remove no-advisor state if it exists
    const noAdvisorElement = this.container.querySelector('.advisor-selector__no-advisor');
    if (noAdvisorElement) {
      noAdvisorElement.remove();
    }

    // Ensure dropdown state exists
    if (!this.container.querySelector('.advisor-selector__button')) {
      const name = localStorage.getItem('Advisor Name') || '';
      const id = localStorage.getItem('Advisor ID') || '';
      let profileImgHtml = '';
      if (profilePicture && profilePicture !== '' && profilePicture !== '-') {
        profileImgHtml = `<div class=\"advisor-selector__profile-picture\"><img src=\"${profilePicture}\" alt=\"${name}\" width=\"40\" height=\"40\" loading=\"lazy\"></div>`;
      }
      const dropdownHTML = `
        <button
          type="button"
          class="advisor-selector__button"
          aria-expanded="false"
          aria-controls="advisor-dropdown"
          data-advisor-trigger
        >
          <span class="advisor-selector__current">
            ${profileImgHtml}
          </span>
          <span class="site-nav__icon-label">
            ${vwAdvisorStr().menuLabel || ''}
            <svg class="advisor-btn-chevron" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
          </span>
        </button>

        <div class="advisor-selector__dropdown" id="advisor-dropdown" data-advisor-dropdown>
          <div class="advisor-selector__actions">
            <a href="${vwLocalizedPath(`/pages/storepoint-bnl/id_${id}`)}" class="advisor-selector__link">
              <div class="advisor-selector__profile-item">
                ${profileImgHtml}
                <div class="advisor-selector__info">
                  <strong>${name}</strong>
                  <span class="advisor-selector__id">${vwAdvisorStr().thermomixAdvisor || ''}</span>
                </div>
              </div>
            </a>
          </div>

          <div class="advisor-selector__actions">
            <a href="${vwLocalizedPath('/pages/choose-from-your-nearest-advisors')}?selected=${id}" class="advisor-selector__link">
              <div class="advisor-selector__action-item">
                <span class="advisor-selector__action-icon">
                  <img src="${vwAdvisorAssets().advisorSvg || ''}" alt="${vwAdvisorStr().findThermomix || ''}" loading="lazy">
                </span>
                <span class="advisor-selector__action-text">${vwAdvisorStr().findThermomix || ''}</span>
                <span class="advisor-selector__action-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </span>
              </div>
            </a>
          </div>

          <div class="advisor-selector__divider"></div>

          <div class="advisor-selector__actions">
            <a href="${this.vorwerkStorePage}" class="advisor-selector__link">
              <div class="advisor-selector__action-item">
                <span class="advisor-selector__action-icon">
                  <img src="${vwAdvisorAssets().locationSvg || ''}" alt="${vwAdvisorStr().findVorwerkStore || ''}" loading="lazy">
                </span>
                <span class="advisor-selector__action-text">${vwAdvisorStr().findVorwerkStore || ''}</span>
                <span class="advisor-selector__action-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </span>
              </div>
            </a>
          </div>

          <div class="advisor-selector__divider"></div>

          <div class="advisor-selector__actions">
            <a href="${vwCustomerSupportUrl()}" class="advisor-selector__link">
              <div class="advisor-selector__action-item">
                <span class="advisor-selector__action-icon">
                  <img src="${vwAdvisorAssets().supportSvg || ''}" alt="${vwAdvisorStr().goSupport || ''}" loading="lazy">
                </span>
                <span class="advisor-selector__action-text">${vwAdvisorStr().goSupport || ''}</span>
                <span class="advisor-selector__action-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </span>
              </div>
            </a>
          </div>
        </div>
      `;

      this.container.innerHTML = dropdownHTML;

      // Re-bind events for new elements
      this.trigger = this.container.querySelector('[data-advisor-trigger]');
      this.dropdown = this.container.querySelector('[data-advisor-dropdown]');

      this.bindEvents();
    }
    else {
      // update dropdown with the latest advisor 
      const name = localStorage.getItem('Advisor Name') || '';
      const id = localStorage.getItem('Advisor ID') || '';
      let storepointId = localStorage.getItem('Advisor Storepoint ID');
      
      let profileImgHtml = localStorage.getItem('Advisor Profile Picture') || '';
      // console.log('update dropdown with the latest advisor',name,id);
      if (profilePicture && profilePicture !== '' && profilePicture !== '-') {
        profileImgHtml = `<div class=\"advisor-selector__profile-picture\"><img src=\"${profilePicture}\" alt=\"${name}\" width=\"40\" height=\"40\" loading=\"lazy\"></div>`;
      }
      else {
        profileImgHtml = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path fill="currentColor" d="M9 14c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0-6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zM9 15c-3.9 0-7 3.1-7 7 1 0 1.9-.6 2.3-1.6.6-2 2.5-3.4 4.7-3.4s4.1 1.4 4.7 3.4c.3 1 1.2 1.6 2.3 1.6 0-3.9-3.1-7-7-7z"/>
              <path fill="currentColor" d="M10 0v4.5c.7.1 1.4.4 2 .8V2h8v5h-2.9l-1.9 1.9c-.8.8-.8 2 0 2.8L17.9 9H22V0H10z"/>
            </svg>`;
      }

      const dropdownHTML = `
      <div class="advisor-selector__actions">
        <a href="${vwLocalizedPath(`/pages/storepoint-bnl/id_${id}`)}" class="advisor-selector__link">
          <div class="advisor-selector__profile-item">
            ${profileImgHtml}
            <div class="advisor-selector__info">
              <strong>${name}</strong>
              <span class="advisor-selector__id">${vwAdvisorStr().thermomixAdvisor || ''}</span>
            </div>
          </div>
        </a>
      </div>

      <div class="advisor-selector__actions">
        <a href="${vwLocalizedPath('/pages/choose-from-your-nearest-advisors')}?selected=${storepointId}" class="advisor-selector__link">
          <div class="advisor-selector__action-item">
            <span class="advisor-selector__action-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <path fill="currentColor" d="M9 14c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0-6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zM9 15c-3.9 0-7 3.1-7 7 1 0 1.9-.6 2.3-1.6.6-2 2.5-3.4 4.7-3.4s4.1 1.4 4.7 3.4c.3 1 1.2 1.6 2.3 1.6 0-3.9-3.1-7-7-7z"/>
                <path fill="currentColor" d="M10 0v4.5c.7.1 1.4.4 2 .8V2h8v5h-2.9l-1.9 1.9c-.8.8-.8 2 0 2.8L17.9 9H22V0H10z"/>
              </svg>
            </span>
            <span class="advisor-selector__action-text">${vwAdvisorStr().findThermomix || ''}</span>
            <span class="advisor-selector__action-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
            </span>
          </div>
        </a>
      </div>

      <div class="advisor-selector__divider"></div>

      <div class="advisor-selector__actions">
        <a href="${this.vorwerkStorePage}" class="advisor-selector__link">
          <div class="advisor-selector__action-item">
            <span class="advisor-selector__action-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" focusable="false" aria-hidden="true"><path d="M23.9949 2.0002C15.6849 2.0002 8.99487 8.6402 8.99487 16.9602C8.99487 23.3202 12.3349 27.6402 23.9949 46.0002C35.6549 27.6402 38.9949 23.3202 38.9949 16.9602C38.9949 8.6402 32.3049 2.0002 23.9949 2.0002ZM23.9949 5.6002C30.2949 5.6002 35.3949 10.6602 35.3949 16.9602C35.3949 21.6802 32.7449 25.4102 23.9949 39.5402C15.2449 25.4102 12.5949 21.6802 12.5949 16.9602C12.5949 10.6602 17.6949 5.6002 23.9949 5.6002ZM23.9949 10.0002C20.1249 10.0002 16.9949 13.1302 16.9949 17.0002C16.9949 20.8702 20.1249 24.0002 23.9949 24.0002C27.8649 24.0002 30.9949 20.8702 30.9949 17.0002C30.9949 13.1302 27.8649 10.0002 23.9949 10.0002ZM23.9949 13.6002C25.8649 13.6002 27.3949 15.1302 27.3949 17.0002C27.3949 18.8702 25.8649 20.4002 23.9949 20.4002C22.1249 20.4002 20.5949 18.8702 20.5949 17.0002C20.5949 15.1302 22.1249 13.6002 23.9949 13.6002Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
            </span>
            <span class="advisor-selector__action-text">${vwAdvisorStr().findVorwerkStore || ''}</span>
            <span class="advisor-selector__action-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
            </span>
          </div>
        </a>
      </div>

      <div class="advisor-selector__divider"></div>

      <div class="advisor-selector__actions">
        <a href="${vwCustomerSupportUrl()}" class="advisor-selector__link">
          <div class="advisor-selector__action-item">
            <span class="advisor-selector__action-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" focusable="false" aria-hidden="true"><path d="M6.20994 9.2002H41.8099V36.0802H24.3299L19.2299 42.8602L14.1299 36.0802H6.18994V9.2002H6.20994ZM9.80994 12.8002V32.4802H15.9299L19.2299 36.8402L22.5299 32.4802H38.1899V12.8002H9.80994Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
            </span>
            <span class="advisor-selector__action-text">${vwAdvisorStr().goSupport || ''}</span>
            <span class="advisor-selector__action-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
            </span>
          </div>
        </a>
      </div>
      `;

      this.dropdown.innerHTML = dropdownHTML; //this part is used to apply the latest dropdown content
    }
  }

  showNoAdvisorState() {
    if (!this.container) return;
    if (this.selector) this.selector.classList.remove('has-advisor-selected');
    // Remove dropdown state if it exists
    const buttonElement = this.container.querySelector('.advisor-selector__button');
    if (buttonElement) {
      buttonElement.remove();
    }

    const dropdownElement = this.container.querySelector('.advisor-selector__dropdown');
    if (dropdownElement) {
      dropdownElement.remove();
    }

    // Ensure no-advisor state exists
    if (!this.container.querySelector('.advisor-selector__no-advisor')) {
      let defaultAdvisorPage = document.querySelector('.jsc-pages-storepoint').value;

      const noAdvisorHTML = `
          <button type="button" class="advisor-selector__button" aria-expanded="false" aria-controls="advisor-dropdown" data-advisor-trigger>
            <span class="advisor-selector__current">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path fill="currentColor" d="M9 14c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0-6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zM9 15c-3.9 0-7 3.1-7 7 1 0 1.9-.6 2.3-1.6.6-2 2.5-3.4 4.7-3.4s4.1 1.4 4.7 3.4c.3 1 1.2 1.6 2.3 1.6 0-3.9-3.1-7-7-7z"/>
                <path fill="currentColor" d="M10 0v4.5c.7.1 1.4.4 2 .8V2h8v5h-2.9l-1.9 1.9c-.8.8-.8 2 0 2.8L17.9 9H22V0H10z"/>
              </svg>
            </span>

            <span class="site-nav__icon-label">
              ${vwAdvisorStr().menuLabel || ''}
              <svg class="advisor-btn-chevron" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </span>
          </button>

          <div class="advisor-selector__dropdown" id="advisor-dropdown" data-advisor-dropdown>
            <div class="advisor-selector__actions">
               <a href="${vwLocalizedPath(defaultAdvisorPage)}" class="advisor-selector__link">
                  <div class="advisor-selector__profile-item">
                    <div class="advisor-selector__profile-picture">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="currentColor">
                        <path d="M24 24c4.42 0 8-3.58 8-8s-3.58-8-8-8-8 3.58-8 8 3.58 8 8 8zm0 4c-5.33 0-16 2.67-16 8v4h32v-4c0-5.33-10.67-8-16-8z"/>
                      </svg>
                    </div>
                    <div class="advisor-selector__info">
                      <strong>${vwAdvisorStr().myAdvisor || ''}</strong>
                      <span class="advisor-selector__id">${vwAdvisorStr().selectAdvisor || ''}</span>
                    </div>
                  </div>
               </a>
            </div>

            <div class="advisor-selector__actions">
              <a href="${vwLocalizedPath('/pages/choose-from-your-nearest-advisors')}?selected=%27%27" class="advisor-selector__link">
                <div class="advisor-selector__action-item">
                  <span class="advisor-selector__action-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M9 14c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0-6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zM9 15c-3.9 0-7 3.1-7 7 1 0 1.9-.6 2.3-1.6.6-2 2.5-3.4 4.7-3.4s4.1 1.4 4.7 3.4c.3 1 1.2 1.6 2.3 1.6 0-3.9-3.1-7-7-7z"/>
                      <path fill="currentColor" d="M10 0v4.5c.7.1 1.4.4 2 .8V2h8v5h-2.9l-1.9 1.9c-.8.8-.8 2 0 2.8L17.9 9H22V0H10z"/>
                    </svg>
                  </span>
                  <span class="advisor-selector__action-text">${vwAdvisorStr().findThermomix || ''}</span>
                  <span class="advisor-selector__action-arrow">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
                  </span>
                </div>
              </a>
            </div>

            <div class="advisor-selector__divider"></div>

            <div class="advisor-selector__actions">
              <a href="${this.vorwerkStorePage}" class="advisor-selector__link">
                <div class="advisor-selector__action-item">
                  <span class="advisor-selector__action-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" focusable="false" aria-hidden="true"><path d="M23.9949 2.0002C15.6849 2.0002 8.99487 8.6402 8.99487 16.9602C8.99487 23.3202 12.3349 27.6402 23.9949 46.0002C35.6549 27.6402 38.9949 23.3202 38.9949 16.9602C38.9949 8.6402 32.3049 2.0002 23.9949 2.0002ZM23.9949 5.6002C30.2949 5.6002 35.3949 10.6602 35.3949 16.9602C35.3949 21.6802 32.7449 25.4102 23.9949 39.5402C15.2449 25.4102 12.5949 21.6802 12.5949 16.9602C12.5949 10.6602 17.6949 5.6002 23.9949 5.6002ZM23.9949 10.0002C20.1249 10.0002 16.9949 13.1302 16.9949 17.0002C16.9949 20.8702 20.1249 24.0002 23.9949 24.0002C27.8649 24.0002 30.9949 20.8702 30.9949 17.0002C30.9949 13.1302 27.8649 10.0002 23.9949 10.0002ZM23.9949 13.6002C25.8649 13.6002 27.3949 15.1302 27.3949 17.0002C27.3949 18.8702 25.8649 20.4002 23.9949 20.4002C22.1249 20.4002 20.5949 18.8702 20.5949 17.0002C20.5949 15.1302 22.1249 13.6002 23.9949 13.6002Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
                  </span>
                  <span class="advisor-selector__action-text">${vwAdvisorStr().findVorwerkStore || ''}</span>
                  <span class="advisor-selector__action-arrow">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
                  </span>
                </div>
              </a>
            </div>

            <div class="advisor-selector__divider"></div>

            <div class="advisor-selector__actions">
              <a href="${vwCustomerSupportUrl()}" class="advisor-selector__link">
                <div class="advisor-selector__action-item">
                  <span class="advisor-selector__action-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" focusable="false" aria-hidden="true"><path d="M6.20994 9.2002H41.8099V36.0802H24.3299L19.2299 42.8602L14.1299 36.0802H6.18994V9.2002H6.20994ZM9.80994 12.8002V32.4802H15.9299L19.2299 36.8402L22.5299 32.4802H38.1899V12.8002H9.80994Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>
                  </span>
                  <span class="advisor-selector__action-text">${vwAdvisorStr().goSupport || ''}</span>
                  <span class="advisor-selector__action-arrow">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>
                  </span>
                </div>
              </a>
            </div>
          </div>
      `;

      this.container.innerHTML = noAdvisorHTML;

      // Re-bind events for new elements
      this.trigger = this.container.querySelector('[data-advisor-trigger]');
      this.dropdown = this.container.querySelector('[data-advisor-dropdown]');
      this.bindEvents();
    }
  }



  toggleDropdown() {
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    if (!this.dropdown || !this.trigger) return;

    this.dropdown.classList.add('is-active');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.setModalOpenState(true);
    this.isOpen = true;
  }

  closeDropdown() {
    if (!this.dropdown || !this.trigger) return;

    this.dropdown.classList.remove('is-active');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.setModalOpenState(false);
    this.isOpen = false;
  }

  setModalOpenState(isOpen) {
    document.documentElement.classList.toggle('advisor-modal-open', isOpen);
  }
}

// Initialize advisor selector when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.advisorSelectorInstance = new AdvisorSelector();
});

document.addEventListener('advisor:selected', function(e) {
  const { id, name, profileImg } = e.detail;

  // Get the AdvisorSelector instance and use its methods
  const advisorSelector = window.advisorSelectorInstance;
  if (advisorSelector) {
    // console.log('AdvisorSelector instance is available');
    // Update display
    advisorSelector.updateDisplay(id, name, profileImg);
    // Close dropdown
    advisorSelector.closeDropdown();
  } else {
    // console.log('AdvisorSelector instance is not available');
  }
});

document.addEventListener('advisor:cleared', function(e) {
  // Get the AdvisorSelector instance and use its methods
  const advisorSelector = window.advisorSelectorInstance;
  if (advisorSelector) {
    // console.log('AdvisorSelector: Advisor data cleared, updating display');
    // Clear hidden inputs
    if (advisorSelector.idInput) advisorSelector.idInput.value = '';
    if (advisorSelector.nameInput) advisorSelector.nameInput.value = '';
    if (advisorSelector.profilePictureInput) advisorSelector.profilePictureInput.value = '';

    // Show no advisor state
    advisorSelector.showNoAdvisorState();

    // Close dropdown if open
    advisorSelector.closeDropdown();
  } else {
    // console.log('AdvisorSelector instance is not available');
  }
});

// Export for use in other modules
window.AdvisorSelector = AdvisorSelector;